import L, { LatLngExpression } from 'leaflet';
import { FC, useEffect, useState } from 'react';
import { MapContainer, TileLayer, ZoomControl, Marker, useMapEvents, Popup } from 'react-leaflet';

import { Box } from '@components';

import { MapProps } from './Map.types';
import MapLegend from '../MapLegend/MapLegend';
import { DEFAULT_HEIGHT, DEFAULT_WIDTH, MAP_TILE_URL, SHOW_MARKERS_ZOOM } from '@common/constants/maps';

import 'leaflet/dist/leaflet.css';

// Fix for missing marker icon
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [24, 36],
  iconAnchor: [12, 36],
});

L.Marker.prototype.options.icon = DefaultIcon;

const Map: FC<MapProps> = ({
  bounds,
  center,
  children,
  height = DEFAULT_HEIGHT,
  legend,
  markers,
  popupPosition,
  popupContent,
  showLegend = true,
  showPopup = false,
  showTile = false,
  showMarkersAlways = false,
  width = DEFAULT_WIDTH,
  zoom,
}) => {
  if (!bounds && !center) {
    throw new Error('Bounds or center props need to be set.');
  }

  const [zoomLevel, setZoomLevel] = useState<number | undefined>(zoom); // initial zoom level provided for MapContainer

  const mapStyle = {
    height,
    width,
    margin: '0 auto',
  };

  const MapEvents: FC<{ onZoom: (z: number) => void }> = ({ onZoom }): null => {
    const mapEvents = useMapEvents({
      zoomend: () => {
        onZoom(mapEvents.getZoom());
      },
    });

    useEffect(() => {
      const zoom = mapEvents.getZoom();
      mapEvents.setZoom(zoom);
      setZoomLevel(zoom);
    }, []);

    return null;
  };

  return (
    <Box css={{ position: 'relative', zIndex: '$map' }}>
      <MapContainer
        bounds={bounds}
        center={center}
        scrollWheelZoom={false}
        style={mapStyle}
        zoomControl={false}
        zoom={zoomLevel}
      >
        {showTile && (
          <Box data-testid="map-tile" id="map-tile">
            <TileLayer url={MAP_TILE_URL} />
          </Box>
        )}
        <ZoomControl position="bottomright" />
        <MapEvents
          onZoom={(zoomVal: number) => {
            setZoomLevel(zoomVal);
          }}
        />
        {children}
        {zoomLevel &&
          (zoomLevel < SHOW_MARKERS_ZOOM || showMarkersAlways) &&
          markers?.map((coordinate: LatLngExpression, index) => {
            return (
              <Box data-testid="map-marker" key={index}>
                <Marker position={coordinate}></Marker>
              </Box>
            );
          })}
        {showPopup && (
          <Popup className="map-popup" data-testid="map-popup" position={popupPosition} closeButton={false}>
            {popupContent}
          </Popup>
        )}
      </MapContainer>
      {showLegend && legend && <MapLegend {...legend} />}
    </Box>
  );
};

export default Map;
